import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { SignOut, UsersThree, ChartLineUp, Lifebuoy, LockKey, LockKeyOpen, PaperPlaneTilt, ShieldStar, Coins, Trash, FloppyDisk, Question, X, Notebook, Moon, CheckCircle, RocketLaunch, Bug, Lightbulb, Wallet } from "@phosphor-icons/react";
import { RoadmapAdminTab, FeedbackAdminTab } from "@/pages/AdminRoadmap";
import { PaymentsTab } from "@/pages/AdminPayments";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatApiErrorDetail } from "@/lib/api";

const card = "bg-[#12151d] border border-[#232733] rounded-xl";
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const AdminLogin = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      const { data: me } = await api.get("/auth/me");
      if (me.role !== "admin") {
        await api.post("/auth/logout");
        toast.error("This account is not an administrator");
        return;
      }
      setUser(me);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className={`${card} w-full max-w-sm p-8`}>
        <BrandMark size={30} dark />
        <div className="mt-4 flex items-center gap-2 text-[#FF6B35] text-xs font-bold uppercase tracking-[0.2em]">
          <ShieldStar size={14} weight="fill" /> Admin Console
        </div>
        <p className="mt-1 text-xs text-[#8a90a0]">Platform staff only — tenant accounts cannot enter.</p>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="admin email"
          data-testid="admin-login-email"
          className="mt-6 w-full h-11 rounded-lg bg-[#0A0D14] border border-[#232733] px-3 text-sm text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]" />
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="password"
          data-testid="admin-login-password"
          className="mt-3 w-full h-11 rounded-lg bg-[#0A0D14] border border-[#232733] px-3 text-sm text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]" />
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="mt-5 w-full h-11 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm transition-colors disabled:opacity-60">
          {busy ? "Signing in…" : "Enter console"}
        </button>
      </form>
    </div>
  );
};

const Kpi = ({ label, value, testid }) => (
  <div className={`${card} p-4`} data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">{label}</div>
    <div className="mt-1 text-2xl font-extrabold text-white">{value}</div>
  </div>
);

const OverviewTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Kpi label="Tenants" value={d.total_workspaces} testid="admin-kpi-tenants" />
        <Kpi label="Users" value={d.total_users} testid="admin-kpi-users" />
        <Kpi label="Signups 7d" value={d.signups_7d} testid="admin-kpi-signups7" />
        <Kpi label="Signups 30d" value={d.signups_30d} testid="admin-kpi-signups30" />
        <Kpi label="Open tickets" value={d.open_tickets} testid="admin-kpi-tickets" />
        <Kpi label="Active paid" value={d.billing.active_paid} testid="admin-kpi-paid" />
        <Kpi label="In grace" value={d.billing.grace} testid="admin-kpi-grace" />
        <Kpi label="Locked" value={d.billing.locked} testid="admin-kpi-locked" />
        <Kpi label="Revenue 30d" value={inr(d.revenue_30d)} testid="admin-kpi-rev30" />
        <Kpi label="Revenue total" value={inr(d.revenue_total)} testid="admin-kpi-revtotal" />
      </div>
      <div className={`${card} p-5`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Sign-ups — last 30 days</h3>
          <div className="flex gap-2 text-[11px] font-bold">
            <span className="px-2.5 py-1 rounded-full bg-[#232733] text-[#c9cfdb]">Free {d.plans.free}</span>
            <span className="px-2.5 py-1 rounded-full bg-[#FF6B35]/15 text-[#FF6B35]">Pro {d.plans.pro}</span>
            <span className="px-2.5 py-1 rounded-full bg-[#8b5cf6]/15 text-[#a78bfa]">Agency {d.plans.agency}</span>
          </div>
        </div>
        <div className="h-52 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.signup_series}>
              <defs>
                <linearGradient id="su" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6B35" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FF6B35" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#5a6172", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#5a6172", fontSize: 10 }} allowDecimals={false} width={26} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#12151d", border: "1px solid #232733", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#c9cfdb" }} />
              <Area type="monotone" dataKey="count" stroke="#FF6B35" strokeWidth={2} fill="url(#su)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const statusPill = (t) => {
  const map = { active: "bg-emerald-500/15 text-emerald-400", grace: "bg-amber-500/15 text-amber-400", locked: "bg-red-500/15 text-red-400" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[t.status] || "bg-[#232733] text-[#8a90a0]"}`}>{t.plan === "free" ? "free" : t.status}{t.manual_lock ? " · manual" : ""}{t.dormant_lock ? " · dormant" : ""}</span>;
};

const TenantsTab = () => {
  const [rows, setRows] = useState(null);
  const [reacts, setReacts] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/admin/tenants").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/admin/reactivations").then(({ data }) => setReacts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, fn, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (!rows) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  return (
    <div className="space-y-4">
    {reacts.length > 0 && (
      <div className={`${card} p-5 border-amber-500/40`} data-testid="admin-reactivations-panel">
        <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
          <Moon size={14} weight="fill" /> Reactivation requests ({reacts.length})
        </h4>
        <div className="mt-3 space-y-2">
          {reacts.map((r) => (
            <div key={r.id} className="flex items-center gap-3 bg-[#0A0D14] rounded-lg px-4 py-2.5 text-xs text-[#c9cfdb]" data-testid={`reactivation-${r.id}`}>
              <span className="font-bold text-white">{r.workspace_name || r.workspace_id}</span>
              <span className="text-[#5a6172]">{r.requested_by}</span>
              <span className="ml-auto text-[#5a6172]">{fmtDate(r.created_at)}</span>
              <button disabled={busy === r.id} data-testid={`reactivation-approve-${r.id}`}
                onClick={() => act(r.id, () => api.post(`/admin/reactivations/${r.id}/approve`), "Workspace reactivated")}
                className="h-7 px-3 rounded-md text-[11px] font-bold inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">
                <CheckCircle size={12} weight="fill" /> Approve
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
    <div className={`${card} overflow-x-auto`} data-testid="admin-tenants-table">
      <table className="w-full text-left text-xs text-[#c9cfdb]">
        <thead>
          <tr className="border-b border-[#232733] text-[10px] uppercase tracking-wider text-[#5a6172]">
            {["Workspace", "Owner", "Signed up", "Auth", "Plan / Status", "Expires", "AI used", "Meta", "Campaigns", "Leads", "Actions"].map((h) => (
              <th key={h} className="px-3 py-3 font-bold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.workspace_id} className="border-b border-[#232733]/60 hover:bg-[#171b25]" data-testid={`tenant-row-${t.workspace_id}`}>
              <td className="px-3 py-3 font-bold text-white whitespace-nowrap">{t.workspace_name || "—"}</td>
              <td className="px-3 py-3 whitespace-nowrap">
                <div className="font-semibold">{t.owner_name || "—"}</div>
                <div className="text-[#5a6172]">{t.owner_email}</div>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">{fmtDate(t.created_at)}</td>
              <td className="px-3 py-3">{t.auth_provider}</td>
              <td className="px-3 py-3 whitespace-nowrap"><span className="font-bold capitalize mr-1.5">{t.plan}</span>{statusPill(t)}</td>
              <td className="px-3 py-3 whitespace-nowrap">{fmtDate(t.plan_expires_at)}</td>
              <td className="px-3 py-3">{t.ai_used}</td>
              <td className="px-3 py-3">{t.meta_connected ? <span className="text-emerald-400 font-bold">✓</span> : <span className="text-[#5a6172]">—</span>}</td>
              <td className="px-3 py-3">{t.campaigns}</td>
              <td className="px-3 py-3">{t.leads}</td>
              <td className="px-3 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <select value={t.plan} disabled={busy === t.workspace_id} data-testid={`tenant-plan-select-${t.workspace_id}`}
                    onChange={(e) => act(t.workspace_id, () => api.post(`/admin/tenants/${t.workspace_id}/plan`, { plan: e.target.value }), `Plan set to ${e.target.value}`)}
                    className="h-7 rounded-md bg-[#0A0D14] border border-[#232733] px-1.5 text-[11px] text-white outline-none">
                    <option value="free">free</option><option value="pro">pro</option><option value="agency">agency</option>
                  </select>
                  <button disabled={busy === t.workspace_id} data-testid={`tenant-extend-7-${t.workspace_id}`}
                    onClick={() => act(t.workspace_id, () => api.post(`/admin/tenants/${t.workspace_id}/extend`, { days: 7 }), "+7 days added")}
                    className="h-7 px-2 rounded-md bg-[#232733] hover:bg-[#2d3342] text-[11px] font-bold">+7d</button>
                  <button disabled={busy === t.workspace_id} data-testid={`tenant-extend-30-${t.workspace_id}`}
                    onClick={() => act(t.workspace_id, () => api.post(`/admin/tenants/${t.workspace_id}/extend`, { days: 30 }), "+30 days added")}
                    className="h-7 px-2 rounded-md bg-[#232733] hover:bg-[#2d3342] text-[11px] font-bold">+30d</button>
                  <button disabled={busy === t.workspace_id} data-testid={`tenant-lock-btn-${t.workspace_id}`}
                    title={t.manual_lock ? "Unlock workspace" : "Lock workspace"}
                    onClick={() => act(t.workspace_id, () => api.post(`/admin/tenants/${t.workspace_id}/lock`, { locked: !t.manual_lock }), t.manual_lock ? "Unlocked" : "Locked")}
                    className={`h-7 px-2 rounded-md text-[11px] font-bold inline-flex items-center gap-1 ${t.manual_lock ? "bg-red-500/15 text-red-400 hover:bg-red-500/25" : "bg-[#232733] hover:bg-[#2d3342]"}`}>
                    {t.manual_lock ? <LockKeyOpen size={12} /> : <LockKey size={12} />}{t.manual_lock ? "Unlock" : "Lock"}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
};

const PricingTab = () => {
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.get("/admin/pricing-doc").then(({ data }) => setDoc(data)).catch(() => {}); }, []);
  if (!doc) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  const planKeys = doc.plans.map((p) => p.key);
  return (
    <div className="space-y-6" data-testid="admin-pricing-doc">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-bold text-white">Plans &amp; Pricing — finalized reference</h3>
        <span className="text-[11px] text-[#5a6172]">Live from current config — updates automatically when you change P&amp;L rates or quotas</span>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {doc.plans.map((p) => (
          <div key={p.key} className={`${card} p-5`} data-testid={`pricing-plan-${p.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-extrabold text-white">{p.name}</span>
              <span className="text-lg font-extrabold text-[#FF6B35]">{p.price === 0 ? "₹0" : inr(p.price / 100)}<span className="text-[10px] text-[#5a6172]">{p.price === 0 ? "" : "/30d"}</span></span>
            </div>
            <div className="mt-2 text-[11px] text-[#8a90a0]">
              Ad accounts: <b className="text-white">{p.ad_accounts ?? "Unlimited"}</b> · Max AI cost to you:{" "}
              <b className={p.max_ai_cost > 100 && p.key === "free" ? "text-red-400" : "text-emerald-400"}>{inr(p.max_ai_cost)}/mo</b>
            </div>
            <ul className="mt-3 space-y-1.5">
              {p.features.map((f) => (
                <li key={f} className="text-[11px] text-[#c9cfdb] flex items-start gap-1.5">
                  <CheckCircle size={13} weight="fill" className="text-[#FF6B35] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className={`${card} overflow-x-auto`} data-testid="pricing-quota-matrix">
        <table className="w-full text-left text-xs text-[#c9cfdb]">
          <thead>
            <tr className="border-b border-[#232733] text-[10px] uppercase tracking-wider text-[#5a6172]">
              <th className="px-3 py-3 font-bold">AI service</th>
              {planKeys.map((k) => <th key={k} className="px-3 py-3 font-bold capitalize">{k} quota</th>)}
              <th className="px-3 py-3 font-bold">Top-up ₹</th>
              <th className="px-3 py-3 font-bold">Your cost ₹</th>
              <th className="px-3 py-3 font-bold">Margin/extra</th>
            </tr>
          </thead>
          <tbody>
            {doc.features.map((f) => (
              <tr key={f.kind} className="border-b border-[#232733]/60" data-testid={`pricing-feature-${f.kind}`}>
                <td className="px-3 py-3">
                  <div className="font-bold text-white">{f.name}</div>
                  <div className="text-[#5a6172] text-[11px] max-w-[300px]">{f.desc}</div>
                </td>
                {planKeys.map((k) => <td key={k} className="px-3 py-3 font-bold">{(f.quotas[k] ?? 0) < 0 ? <span className="text-emerald-400">Unltd</span> : `${f.quotas[k] ?? 0}/mo`}</td>)}
                <td className="px-3 py-3 font-bold text-[#FF6B35]">{Object.values(f.quotas).some((q) => q < 0) ? "—" : `₹${f.topup_price}`}</td>
                <td className="px-3 py-3">₹{f.cost_rate}</td>
                <td className="px-3 py-3 font-bold text-emerald-400">₹{Math.round((f.topup_price - f.cost_rate) * 100) / 100}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={`${card} p-5`} data-testid="pricing-policies">
        <h4 className="text-xs font-bold text-white uppercase tracking-wider">Billing policies</h4>
        <ul className="mt-3 space-y-2">
          {doc.policies.map((p, i) => (
            <li key={i} className="text-[11px] text-[#c9cfdb] flex items-start gap-2">
              <span className="text-[#FF6B35] font-bold shrink-0">{String(i + 1).padStart(2, "0")}</span> {p}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[10px] text-[#5a6172]">Top-up packs: {doc.packs.map((p) => `+${p}`).join(" · ")} credits per purchase. Quotas &amp; top-up prices are editable via platform_settings (feature_pricing); cost rates via the P&amp;L tab.</p>
      </div>
    </div>
  );
};

const TicketsTab = ({ refreshBadge }) => {
  const [tickets, setTickets] = useState(null);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const load = useCallback(() => {
    api.get("/admin/tickets").then(({ data }) => {
      setTickets(data);
      setSel((s) => (s ? data.find((t) => t.id === s.id) || null : null));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!reply.trim() || !sel) return;
    try {
      await api.post(`/admin/tickets/${sel.id}/reply`, { message: reply });
      setReply(""); load(); refreshBadge();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    try { await api.post(`/admin/tickets/${sel.id}/status`, { status }); load(); refreshBadge(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!tickets) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  if (tickets.length === 0) return <div className={`${card} p-8 text-sm text-[#8a90a0]`} data-testid="admin-tickets-empty">No support tickets yet.</div>;
  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-4" data-testid="admin-tickets">
      <div className={`${card} divide-y divide-[#232733]/60 max-h-[70vh] overflow-y-auto`}>
        {tickets.map((t) => (
          <button key={t.id} onClick={() => setSel(t)} data-testid={`admin-ticket-item-${t.id}`}
            className={`w-full text-left px-4 py-3 hover:bg-[#171b25] ${sel?.id === t.id ? "bg-[#171b25]" : ""}`}>
            <div className="flex items-center gap-2">
              {t.status === "open" && t.last_message_by === "tenant" && <span className="h-2 w-2 rounded-full bg-[#FF6B35] shrink-0" />}
              <span className="text-sm font-bold text-white truncate">{t.subject}</span>
              <span className={`ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${t.status === "open" ? "bg-emerald-500/15 text-emerald-400" : "bg-[#232733] text-[#8a90a0]"}`}>{t.status}</span>
            </div>
            <div className="mt-0.5 text-[11px] text-[#5a6172] truncate">{t.workspace_name || t.user_email} · {fmtDate(t.updated_at)}</div>
          </button>
        ))}
      </div>
      {sel ? (
        <div className={`${card} p-5 flex flex-col max-h-[70vh]`}>
          <div className="flex items-center gap-3 pb-3 border-b border-[#232733]">
            <div>
              <h3 className="text-sm font-bold text-white">{sel.subject}</h3>
              <p className="text-[11px] text-[#5a6172]">{sel.user_name} · {sel.user_email} · {sel.workspace_name}</p>
            </div>
            <button onClick={() => setStatus(sel.status === "open" ? "closed" : "open")} data-testid="admin-ticket-close"
              className="ml-auto h-8 px-3 rounded-full bg-[#232733] hover:bg-[#2d3342] text-[11px] font-bold text-[#c9cfdb]">
              {sel.status === "open" ? "Close ticket" : "Reopen"}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {sel.messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.by === "admin" ? "ml-auto bg-[#FF6B35]/15 text-[#ffd9c7]" : "bg-[#232733] text-[#c9cfdb]"}`}>
                <div className="text-[10px] font-bold text-[#8a90a0] mb-0.5">{m.by === "admin" ? m.author : `${sel.user_name || "Tenant"}`} · {new Date(m.at).toLocaleString("en-IN")}</div>
                {m.text}
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-3 border-t border-[#232733]">
            <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Reply to tenant…" data-testid="admin-ticket-reply-input"
              className="flex-1 h-10 rounded-lg bg-[#0A0D14] border border-[#232733] px-3 text-sm text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]" />
            <button onClick={send} data-testid="admin-ticket-reply-send"
              className="h-10 px-4 rounded-lg bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm inline-flex items-center gap-1.5">
              <PaperPlaneTilt size={14} weight="fill" /> Send
            </button>
          </div>
        </div>
      ) : (
        <div className={`${card} p-8 text-sm text-[#5a6172] flex items-center justify-center`}>Select a ticket to view the thread</div>
      )}
    </div>
  );
};

const PNL_HELP = [
  { term: "Revenue", def: "Money actually collected from a tenant through Razorpay for their plan (₹2,499 for Pro, ₹7,999 for Agency, per 30 days). Only successful payments count, and they are counted in the month the payment was made. Free-plan tenants generate ₹0 revenue." },
  { term: "Analysis (run)", def: "One instance of the AI studying a tenant's ad-account performance and writing an insights/recommendations report. Runs on the Claude AI model — each run costs us the rate set in Cost settings." },
  { term: "Automation (run)", def: "One scheduled wake-up of the Automation Agent: it reviews the tenant's campaigns and proposes or executes optimisations (budgets, pausing weak ads, etc.). Also Claude-powered; billed to us per run." },
  { term: "Creative (job)", def: "One complete ad-creative production: the AI designs the ad image (Gemini), writes the ad copy (Claude) and stamps the tenant's brand logo. This is the most expensive single action because it uses both an image model and a text model." },
  { term: "Chat (message)", def: "One message handled by the AI Creative Team (Arjun/Riya) when a tenant gives feedback or asks for changes on a creative. Light Claude usage, cheapest action." },
  { term: "Meera chat (message)", def: "One message to Meera, the AI copilot on the tenant's AI Agent page — she can read live campaign data and act on the approval queue. Each message is a Claude call, priced per message like team chat." },
  { term: "AI cost", def: "What the tenant's activity costs US: (number of each action above) × (the ₹ rate per action from Cost settings). The rates are our estimated cost of the underlying AI models — adjust them anytime and the whole P&L recalculates." },
  { term: "Overhead", def: "Our fixed monthly cost of running the platform (hosting, infrastructure, tools). Entered once in Cost settings; applied to the business as a whole, not to individual tenants." },
  { term: "Other expenses", def: "Any manual cost you add for a given month — e.g., marketing spend, freelancer fees. Added with the 'Add' button; each entry belongs to the selected month." },
  { term: "Profit (per tenant)", def: "Revenue from that tenant minus their AI cost. A negative (red) number means that tenant currently costs us more than they pay — common for Free-plan users." },
  { term: "Net profit (global)", def: "Total revenue − total AI cost − overhead − other expenses for the selected month. The single number that says whether the business made or lost money that month." },
  { term: "Margin", def: "Profit as a percentage of revenue. Example: revenue ₹10,000 with ₹4,000 total costs = ₹6,000 profit = 60% margin. Shows '—' when there is no revenue." },
];

const PnlHelpModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="pnl-help-modal">
    <div className={`${card} w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <Question size={18} weight="fill" className="text-[#FF6B35]" />
        <h3 className="text-sm font-bold text-white">P&amp;L glossary — how to read this report</h3>
        <button onClick={onClose} data-testid="pnl-help-close" className="ml-auto p-1.5 rounded-md hover:bg-[#232733] text-[#8a90a0] hover:text-white"><X size={16} /></button>
      </div>
      <p className="mt-2 text-xs text-[#8a90a0]">Written for finance &amp; accounts teammates — no technical background needed. The simple formula: <b className="text-white">Profit = what a tenant pays us − what their AI usage costs us.</b></p>
      <div className="mt-5 space-y-4">
        {PNL_HELP.map((h) => (
          <div key={h.term}>
            <div className="text-xs font-bold text-[#FF6B35] uppercase tracking-wider">{h.term}</div>
            <p className="mt-1 text-sm text-[#c9cfdb] leading-relaxed">{h.def}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const RATE_LABELS = { analysis: "Analysis run", agent_run: "Automation run", creative_job: "Creative job", team_chat: "Team chat msg", meera_chat: "Meera chat msg", image_regen: "Image regen", whatsapp_msg: "WhatsApp msg" };
const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const PnlTab = () => {
  const [month, setMonth] = useState(monthOptions()[0]);
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({});
  const [overhead, setOverhead] = useState("");
  const [expLabel, setExpLabel] = useState("");
  const [expAmt, setExpAmt] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const load = useCallback(() => {
    api.get(`/admin/pnl?month=${month}`).then(({ data: d }) => {
      setData(d);
      setRates(d.settings.ai_rates);
      setOverhead(String(d.settings.monthly_overhead));
    }).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const saveSettings = async () => {
    try {
      await api.post("/admin/pnl/settings", { ai_rates: rates, monthly_overhead: Number(overhead) || 0 });
      toast.success("Cost settings saved"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addExpense = async () => {
    try {
      await api.post("/admin/pnl/expenses", { month, label: expLabel, amount: Number(expAmt) });
      setExpLabel(""); setExpAmt(""); toast.success("Expense added"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delExpense = async (id) => {
    try { await api.delete(`/admin/pnl/expenses/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!data) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  const t = data.totals;
  const profitColor = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-white");
  return (
    <div className="space-y-6" data-testid="admin-pnl">
      {showHelp && <PnlHelpModal onClose={() => setShowHelp(false)} />}
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-bold text-white">Profit &amp; Loss</h3>
        <button onClick={() => setShowHelp(true)} data-testid="pnl-help-btn"
          className="h-7 px-3 rounded-full bg-[#232733] hover:bg-[#2d3342] text-[11px] font-bold text-[#c9cfdb] inline-flex items-center gap-1.5">
          <Question size={13} weight="fill" /> How to read this
        </button>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="pnl-month-select"
          className="ml-auto h-9 rounded-lg bg-[#12151d] border border-[#232733] px-3 text-xs font-bold text-white outline-none">
          {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi label="Revenue" value={inr(t.revenue)} testid="pnl-kpi-revenue" />
        <Kpi label="AI cost" value={inr(t.ai_cost)} testid="pnl-kpi-aicost" />
        <Kpi label="Overhead" value={inr(t.overhead)} testid="pnl-kpi-overhead" />
        <Kpi label="Other expenses" value={inr(t.expenses)} testid="pnl-kpi-expenses" />
        <div className={`${card} p-4`} data-testid="pnl-kpi-net">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Net profit</div>
          <div className={`mt-1 text-2xl font-extrabold ${profitColor(t.net_profit)}`}>{inr(t.net_profit)}</div>
        </div>
        <Kpi label="Margin" value={t.margin === null ? "—" : `${t.margin}%`} testid="pnl-kpi-margin" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className={`${card} p-5`}>
          <h4 className="text-xs font-bold text-white uppercase tracking-wider">Cost settings (₹ per action)</h4>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {Object.keys(RATE_LABELS).map((k) => (
              <label key={k} className="text-[11px] font-bold text-[#8a90a0]">
                {RATE_LABELS[k]}
                <input type="number" min="0" value={rates[k] ?? ""} data-testid={`pnl-rate-${k}`}
                  onChange={(e) => setRates({ ...rates, [k]: Number(e.target.value) })}
                  className="mt-1 w-full h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-2.5 text-sm text-white outline-none focus:border-[#FF6B35]" />
              </label>
            ))}
            <label className="text-[11px] font-bold text-[#8a90a0] col-span-2">
              Fixed monthly overhead (infra, hosting…)
              <input type="number" min="0" value={overhead} data-testid="pnl-overhead-input"
                onChange={(e) => setOverhead(e.target.value)}
                className="mt-1 w-full h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-2.5 text-sm text-white outline-none focus:border-[#FF6B35]" />
            </label>
          </div>
          <button onClick={saveSettings} data-testid="pnl-save-settings"
            className="mt-4 h-9 px-4 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold inline-flex items-center gap-1.5">
            <FloppyDisk size={14} /> Save settings
          </button>
        </div>
        <div className={`${card} p-5`}>
          <h4 className="text-xs font-bold text-white uppercase tracking-wider">Other expenses — {month}</h4>
          <div className="mt-4 flex gap-2">
            <input value={expLabel} onChange={(e) => setExpLabel(e.target.value)} placeholder="Label (e.g., Marketing)"
              data-testid="pnl-expense-label"
              className="flex-1 h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-2.5 text-sm text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]" />
            <input value={expAmt} onChange={(e) => setExpAmt(e.target.value)} type="number" min="1" placeholder="₹"
              data-testid="pnl-expense-amount"
              className="w-28 h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-2.5 text-sm text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]" />
            <button onClick={addExpense} data-testid="pnl-expense-add"
              className="h-9 px-4 rounded-lg bg-[#232733] hover:bg-[#2d3342] text-xs font-bold text-white">Add</button>
          </div>
          <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
            {data.expenses.length === 0 && <p className="text-xs text-[#5a6172]">No manual expenses this month.</p>}
            {data.expenses.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs text-[#c9cfdb] bg-[#0A0D14] rounded-lg px-3 py-2" data-testid={`pnl-expense-${e.id}`}>
                <span className="font-semibold">{e.label}</span>
                <span className="ml-auto font-bold">{inr(e.amount)}</span>
                <button onClick={() => delExpense(e.id)} data-testid={`pnl-expense-del-${e.id}`} className="p-1 rounded hover:bg-red-500/10 text-[#5a6172] hover:text-red-400"><Trash size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`${card} overflow-x-auto`} data-testid="pnl-tenants-table">
        <table className="w-full text-left text-xs text-[#c9cfdb]">
          <thead>
            <tr className="border-b border-[#232733] text-[10px] uppercase tracking-wider text-[#5a6172]">
              {["Tenant", "Plan", "Revenue", "Top-ups", "Analysis", "Automation", "Creative", "Chat", "Meera", "Image", "WhatsApp", "AI cost", "Profit", "Margin"].map((h) => (
                <th key={h} className="px-3 py-3 font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tenants.length === 0 && (
              <tr><td colSpan={14} className="px-3 py-6 text-center text-[#5a6172]">No revenue or AI activity in {month}.</td></tr>
            )}
            {data.tenants.map((r) => (
              <tr key={r.workspace_id} className="border-b border-[#232733]/60 hover:bg-[#171b25]" data-testid={`pnl-row-${r.workspace_id}`}>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="font-bold text-white">{r.workspace_name || "—"}</div>
                  <div className="text-[#5a6172]">{r.owner_email}</div>
                </td>
                <td className="px-3 py-3 capitalize font-bold">{r.plan}</td>
                <td className="px-3 py-3 font-bold">{inr(r.revenue)}</td>
                <td className="px-3 py-3 text-emerald-400">{r.topup_revenue > 0 ? inr(r.topup_revenue) : "—"}</td>
                <td className="px-3 py-3">{r.ops.analysis || 0}</td>
                <td className="px-3 py-3">{r.ops.agent_run || 0}</td>
                <td className="px-3 py-3">{r.ops.creative_job || 0}</td>
                <td className="px-3 py-3">{r.ops.team_chat || 0}</td>
                <td className="px-3 py-3">{r.ops.meera_chat || 0}</td>
                <td className="px-3 py-3">{r.ops.image_regen || 0}</td>
                <td className="px-3 py-3">{r.ops.whatsapp_msg || 0}</td>
                <td className="px-3 py-3">{inr(r.ai_cost)}</td>
                <td className={`px-3 py-3 font-extrabold ${profitColor(r.profit)}`}>{inr(r.profit)}</td>
                <td className="px-3 py-3">{r.margin === null ? "—" : `${r.margin}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TABS = [
  { key: "overview", label: "Dashboard", icon: ChartLineUp },
  { key: "tenants", label: "Tenants", icon: UsersThree },
  { key: "pnl", label: "P&L", icon: Coins },
  { key: "pricing", label: "Pricing", icon: Notebook },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "roadmap", label: "Roadmap", icon: RocketLaunch },
  { key: "bugs", label: "Bugs", icon: Bug },
  { key: "features", label: "Features", icon: Lightbulb },
  { key: "tickets", label: "Tickets", icon: Lifebuoy },
];

const AdminConsole = ({ user, logout }) => {
  const [tab, setTab] = useState("overview");
  const [badge, setBadge] = useState(0);
  const refreshBadge = useCallback(() => {
    api.get("/admin/tickets/badge").then(({ data }) => setBadge(data.count)).catch(() => {});
  }, []);
  useEffect(() => { refreshBadge(); }, [refreshBadge, tab]);
  return (
    <div className="min-h-screen bg-[#0A0D14]" data-testid="admin-console">
      <header className="border-b border-[#232733] px-6 py-4 flex items-center gap-6">
        <BrandMark size={26} dark />
        <span className="flex items-center gap-1.5 text-[#FF6B35] text-[10px] font-bold uppercase tracking-[0.2em]"><ShieldStar size={13} weight="fill" /> Admin Console</span>
        <nav className="flex items-center gap-1 ml-4">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`relative h-9 px-4 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${tab === key ? "bg-[#FF6B35] text-white" : "text-[#8a90a0] hover:text-white hover:bg-[#171b25]"}`}>
              <Icon size={14} /> {label}
              {key === "tickets" && badge > 0 && (
                <span data-testid="admin-tickets-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{badge}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3 text-xs text-[#8a90a0]">
          <span data-testid="admin-user-email">{user.email}</span>
          <button onClick={logout} data-testid="admin-logout" title="Log out" className="p-1.5 rounded-md hover:bg-red-500/10 hover:text-red-400"><SignOut size={16} /></button>
        </div>
      </header>
      <main className="px-6 py-6 max-w-[1500px] mx-auto">
        {tab === "overview" && <OverviewTab />}
        {tab === "tenants" && <TenantsTab />}
        {tab === "pnl" && <PnlTab />}
        {tab === "pricing" && <PricingTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "roadmap" && <RoadmapAdminTab />}
        {tab === "bugs" && <FeedbackAdminTab type="BUG" />}
        {tab === "features" && <FeedbackAdminTab type="FEATURE_REQUEST" />}
        {tab === "tickets" && <TicketsTab refreshBadge={refreshBadge} />}
      </main>
    </div>
  );
};

export default function AdminPortal() {
  const { user, setUser, logout } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-[3px] border-[#FF6B35] border-t-transparent animate-spin" /></div>;
  if (user === false) return <AdminLogin setUser={setUser} />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center px-6" data-testid="admin-denied">
        <div className={`${card} p-8 max-w-sm text-center`}>
          <p className="text-sm text-[#c9cfdb]">You're signed in as <b className="text-white">{user.email}</b>, which is not an admin account.</p>
          <button onClick={logout} className="mt-4 h-10 px-5 rounded-full bg-[#FF6B35] text-white text-sm font-bold" data-testid="admin-denied-logout">Switch account</button>
        </div>
      </div>
    );
  }
  return <AdminConsole user={user} logout={logout} />;
}
